import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, Star, ShieldCheck, Clock, HandHeart, Sparkles, Route, Globe, Users, Heart, MessageCircle, Compass, Home as HomeIcon, Briefcase, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { SERVICES, PROCESS, TESTIMONIALS, PARTNERS, IMG } from "@/data/content";

const WHO = [
  { icon: Users, title: "Families", desc: "Helping protect the people who matter most to you." },
  { icon: HomeIcon, title: "First-home buyers", desc: "Making sense of the insurance that often comes with buying a home." },
  { icon: Briefcase, title: "Professionals", desc: "Looking after your income, your health and your future plans." },
  { icon: User, title: "Individuals", desc: "Cover that fits your life, your health and your plans." },
];

const TRUST = [
  { icon: ShieldCheck, title: "Clear Advice", desc: "No jargon. Insurance explained so it actually makes sense." },
  { icon: Users, title: "Personal Service", desc: "Someone who takes the time to get to know you." },
  { icon: Globe, title: "In Your Language", desc: "Support in the language you're comfortable with, if you need it." },
];

const PILLARS = [
  { icon: ShieldCheck, title: "Clear Advice", desc: "No jargon. Insurance explained so it actually makes sense." },
  { icon: Users, title: "Personal Relationships", desc: "You'll work with someone who takes the time to get to know you and your family." },
  { icon: Heart, title: "Ongoing Support", desc: "We're here before, during and after your policy starts, including yearly reviews and a hand at claim time." },
  { icon: Globe, title: "In Your Language", desc: "If it's easier to talk things through in another language, we can help with that too." },
];

export default function Home() {
  return (
    <div className="overflow-hidden">
      <Seo
        title="Quantum Cover | Clear, Personal Insurance Advice NZ"
        description="Clear, honest insurance help for people across New Zealand. Understand your options for life, health, trauma, income protection, TPD and mortgage protection insurance, plus KiwiSaver guidance."
        path="/"
      />
      {/* HERO */}
      <section className="relative bg-[#0B3D2E] text-white pt-32 pb-28 lg:pt-44 lg:pb-36">
        <div className="absolute inset-0 qc-grid-bg opacity-30" />
        <div className="absolute -top-24 -right-24 h-[30rem] w-[30rem] rounded-full bg-emerald-500/20 blur-3xl qc-blob" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center">
          <div className="qc-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm text-emerald-300 mb-7">
              <Globe size={15} /> Insurance made clear and personal
            </span>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              Insurance advice in <span className="text-emerald-400">your preferred language.</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-emerald-50/80 max-w-xl leading-relaxed">
              Insurance should be simple. We're here to help you understand your options, answer your questions, and protect what matters most.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/booking">
                <Button data-testid="hero-consultation-btn" size="lg" className="w-full sm:w-auto rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B3D2E] font-semibold px-8 py-7 text-base">
                  Book a Free Consultation <ArrowRight size={18} className="ml-1.5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button data-testid="hero-explore-btn" size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-white/25 bg-white/5 text-white hover:bg-white/10 hover:text-white px-8 py-7 text-base font-medium">
                  Explore Insurance Options
                </Button>
              </Link>
            </div>
            <p className="mt-7 text-sm text-emerald-100/60 flex items-center gap-2">
              <Check size={16} className="text-emerald-400" /> No obligation · Free · Friendly local advice
            </p>
          </div>

          <div className="relative qc-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img src={IMG.family} alt="A New Zealand family relaxing together at home" fetchPriority="high" className="w-full h-[500px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 max-w-[260px]">
              <span className="grid place-items-center h-11 w-11 rounded-full bg-emerald-100 text-emerald-700 shrink-0"><ShieldCheck size={22} /></span>
              <div>
                <p className="font-display font-semibold text-[#0B3D2E] text-sm leading-tight">Made simple</p>
                <p className="text-xs text-slate-500 mt-0.5">Insurance without the jargon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {TRUST.map((t) => (
            <div key={t.title} data-testid="trust-indicator" className="flex items-start gap-3.5">
              <span className="grid place-items-center h-12 w-12 shrink-0 rounded-2xl bg-emerald-50 text-emerald-600"><t.icon size={22} /></span>
              <div>
                <p className="font-display font-semibold text-[#0B3D2E] text-[15px]">{t.title}</p>
                <p className="text-sm text-slate-500 leading-snug mt-0.5">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WE SPEAK YOUR LANGUAGE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
              <img src="/conversation.webp" alt="A Quantum Cover adviser talking things through with a New Zealand couple at home" loading="lazy" className="w-full h-[460px] object-cover" />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600 mb-4">In your language</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#0B3D2E] tracking-tight leading-[1.05]">
              Support in the language you're most comfortable with
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Big insurance decisions are hard enough without a language barrier getting in the way. We'll make the process easier to follow, and if you'd like, help you talk it through in the language you're most comfortable using.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              You'll know what you're covered for, what you're not, and why it matters. No guessing, and no nodding along to words you don't quite get.
            </p>
            <Link to="/booking">
              <Button className="mt-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-6 text-base">Book a Consultation <ArrowRight size={17} className="ml-1.5" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY QUANTUM COVER — 4 PILLARS */}
      <section className="bg-white py-24 lg:py-32 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600 mb-4">Why Quantum Cover</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#0B3D2E] tracking-tight">We're advisers first, not salespeople</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p) => (
              <div key={p.title} data-testid="brand-pillar" className="rounded-3xl bg-slate-50 border border-slate-100 p-8 hover:-translate-y-1 hover:shadow-[0_16px_50px_rgba(0,0,0,0.06)] transition-[transform,box-shadow] duration-300">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[#0B3D2E] text-emerald-400 mb-6"><p.icon size={24} /></span>
                <h3 className="font-display text-xl font-semibold text-[#0B3D2E]">{p.title}</h3>
                <p className="mt-2.5 text-slate-600 leading-relaxed text-[15px]">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600 mb-4">Insurance options</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#0B3D2E] tracking-tight">Cover and support, explained properly</h2>
          <p className="mt-5 text-lg text-slate-600">From life and health cover to income protection, TPD and KiwiSaver, we'll help you understand what suits you. Then you can have a chat with a licensed adviser when you're ready.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => {
            const Icon = Icons[s.icon] || Icons.Shield;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group bg-white rounded-3xl border border-slate-200 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_16px_50px_rgba(0,0,0,0.08)] transition-[transform,box-shadow] duration-300"
              >
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[#0B3D2E] text-emerald-400 mb-5 group-hover:scale-105 transition-transform"><Icon size={24} /></span>
                <h3 className="font-display text-xl font-semibold text-[#0B3D2E]">{s.name}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">{s.tagline}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={15} /></span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="bg-white border-y border-slate-100 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600 mb-4">Who we help</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#0B3D2E] tracking-tight">Here for people at every stage</h2>
            <p className="mt-5 text-lg text-slate-600">Sorting insurance for the first time, or having another look at cover you already have? Either way, we'll make it easier to get your head around.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHO.map((w) => (
              <div key={w.title} data-testid={`who-card-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-3xl bg-slate-50 border border-slate-100 p-8 hover:-translate-y-1 hover:shadow-[0_16px_50px_rgba(0,0,0,0.06)] transition-[transform,box-shadow] duration-300">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-600 mb-6"><w.icon size={24} /></span>
                <h3 className="font-display text-xl font-semibold text-[#0B3D2E]">{w.title}</h3>
                <p className="mt-2.5 text-slate-600 leading-relaxed text-[15px]">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NOT SURE WHAT COVER — JOURNEY CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 lg:pb-32">
        <div className="relative rounded-[2.5rem] bg-emerald-50 border border-emerald-100 overflow-hidden px-8 py-16 lg:p-20">
          <div className="absolute -top-16 -right-10 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl" />
          <div className="relative grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-emerald-700 text-sm font-semibold mb-4"><Compass size={16} /> Takes about 2 minutes</span>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#0B3D2E] tracking-tight leading-[1.05]">Not sure what cover you need?</h2>
              <p className="mt-5 text-lg text-slate-600 max-w-lg">Answer a handful of quick questions and we'll point you to the cover that's worth talking through with an adviser. It's free, and there's no pressure.</p>
              <Link to="/journey">
                <Button size="lg" data-testid="home-journey-btn" className="mt-9 rounded-full bg-[#0B3D2E] hover:bg-[#124A35] text-white px-8 py-7 text-base">Find the right cover <ArrowRight size={18} className="ml-1.5" /></Button>
              </Link>
            </div>
            <div className="hidden lg:block">
              <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-black/5">
                <img src="/planning.webp" alt="A New Zealand couple reviewing their plans together over coffee at home" loading="lazy" className="w-full h-72 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#0B3D2E] text-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400 mb-4">How it works</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">How it works, start to finish</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p) => (
              <div key={p.step} className="rounded-3xl bg-white/5 border border-white/10 p-8 hover:bg-white/[0.08] transition-colors">
                <span className="font-display text-4xl font-bold text-emerald-400/40">{p.step}</span>
                <h3 className="font-display text-lg font-semibold mt-3">{p.title}</h3>
                <p className="mt-2 text-emerald-50/60 text-[15px] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600 mb-4">What people say</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#0B3D2E] tracking-tight">Real Kiwis, real help</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} data-testid="testimonial-card" className="bg-white rounded-3xl border border-slate-200 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <div className="flex text-amber-400 mb-4">{[...Array(t.rating)].map((_,i)=><Star key={i} size={16} fill="currentColor" />)}</div>
              <p className="text-slate-700 leading-relaxed">"{t.text}"</p>
              <div className="mt-6 pt-5 border-t border-slate-100">
                <p className="font-display font-semibold text-[#0B3D2E]">{t.name}</p>
                <p className="text-sm text-slate-500">{t.location}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4">
          <span className="text-sm text-slate-500">In partnership with our licensed provider:</span>
          {PARTNERS.map((p) => (
            <div key={p.name} className="flex items-center gap-2.5">
              <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#0B3D2E] text-emerald-400 font-display font-bold text-sm">{p.name[0]}</span>
              <div><p className="font-display font-semibold text-[#0B3D2E] text-sm leading-tight">{p.name}</p><p className="text-xs text-slate-500">{p.role}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28">
        <div className="relative rounded-[2.5rem] bg-[#0B3D2E] overflow-hidden px-8 py-16 lg:p-20">
          <div className="absolute -bottom-24 -right-16 h-96 w-96 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-4xl sm:text-5xl font-semibold text-white tracking-tight leading-[1.05]">Ready to feel sorted about your cover?</h2>
            <p className="mt-5 text-lg text-emerald-50/70">Book a free, no-pressure chat with an adviser who speaks your language. Or answer a few quick questions first and we'll point you in the right direction.</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/booking"><Button size="lg" className="w-full sm:w-auto rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B3D2E] font-semibold px-8 py-7 text-base">Book a Free Consultation</Button></Link>
              <Link to="/journey"><Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-white/25 bg-white/5 text-white hover:bg-white/10 hover:text-white px-8 py-7 text-base">Answer a Few Questions</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
